// ─────────────────────────────────────────────────────────────────────────────
// Sign in with Apple — WEB variant.
//
// The iOS Capacitor build uses `@capacitor-community/apple-sign-in` via the
// separate `AppleSignInButton.jsx`. Web browsers use Apple's hosted JS SDK
// (`appleid.auth.js`). Both flows converge on the same backend endpoint —
// `POST /api/auth/apple` — which verifies the identityToken and issues a
// session cookie.
//
// This component:
//   1. Renders NOTHING when we're on native iOS (Capacitor build) — that
//      surface uses the native provider directly.
//   2. Renders NOTHING when `REACT_APP_APPLE_WEB_SERVICES_ID` is unset —
//      keeps preview / dev environments quiet until Apple Developer config
//      is provisioned.
//   3. On mount, lazy-loads the Apple JS SDK from Apple's CDN and calls
//      `AppleID.auth.init` with our Services ID + redirectURI + random
//      state/nonce. Popup mode (`usePopup: true`) so we can capture the
//      identity token in-line without a redirect round-trip.
//   4. On click, opens Apple's auth popup, extracts `authorization.id_token`,
//      and POSTs it to `/api/auth/apple` alongside the client-supplied
//      email/name (Apple only sends those on the first authorization).
//   5. On success, dispatches into the same `post_auth_redirect` shim the
//      Google flow already uses.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { isNativeIOS } from "../lib/platform";

const APPLE_JS_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

const SERVICES_ID = (process.env.REACT_APP_APPLE_WEB_SERVICES_ID || "").trim();
// May be a single URL or a comma-separated list. We pick the entry whose
// origin matches `window.location.origin` at runtime — that way the same
// build can support production (bilateralcalm.com) AND the preview URL,
// each with its own Apple-registered return URL, without a code fork.
const REDIRECT_URI_LIST = (process.env.REACT_APP_APPLE_WEB_REDIRECT_URI || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

const pickRedirectUri = () => {
  if (typeof window === "undefined") return REDIRECT_URI_LIST[0] || "";
  const origin = window.location.origin;
  const match = REDIRECT_URI_LIST.find((u) => u.startsWith(origin));
  return match || REDIRECT_URI_LIST[0] || "";
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// URL-safe base64 random for CSRF `state` and OIDC `nonce`.
const randomToken = () => {
  const buf = new Uint8Array(24);
  window.crypto.getRandomValues(buf);
  return btoa(String.fromCharCode(...buf))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");
};

const loadAppleSdk = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.AppleID?.auth) return resolve();
    const existing = document.querySelector(`script[src="${APPLE_JS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Apple JS failed to load")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = APPLE_JS_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Apple JS failed to load"));
    document.head.appendChild(s);
  });

export const AppleSignInWebButton = ({ onSuccess, label = "Continue with Apple" }) => {
  const inited = useRef(false);
  const nonceRef = useRef(null);
  const [busy, setBusy] = useState(false);

  // Hide on native iOS or when the web Services ID isn't configured yet.
  const redirectUri = pickRedirectUri();
  const shouldRender = !isNativeIOS() && !!SERVICES_ID && !!redirectUri;

  useEffect(() => {
    if (!shouldRender || inited.current) return;
    let cancelled = false;
    loadAppleSdk()
      .then(() => {
        if (cancelled || !window.AppleID?.auth) return;
        const nonce = randomToken();
        nonceRef.current = nonce;
        window.AppleID.auth.init({
          clientId: SERVICES_ID,
          scope: "name email",
          redirectURI: redirectUri,
          state: randomToken(),
          nonce,
          usePopup: true,
        });
        inited.current = true;
      })
      .catch(() => { /* button will surface the error at click-time */ });
    return () => { cancelled = true; };
  }, [shouldRender, redirectUri]);

  if (!shouldRender) return null;

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (!window.AppleID?.auth || !inited.current) {
        await loadAppleSdk();
        // If the effect hasn't fired yet, initialise inline.
        if (!inited.current && window.AppleID?.auth) {
          const nonce = randomToken();
          nonceRef.current = nonce;
          window.AppleID.auth.init({
            clientId: SERVICES_ID,
            scope: "name email",
            redirectURI: redirectUri,
            state: randomToken(),
            nonce,
            usePopup: true,
          });
          inited.current = true;
        }
      }
      const result = await window.AppleID.auth.signIn();
      const idToken = result?.authorization?.id_token;
      if (!idToken) throw new Error("Apple returned no identity token");
      // Apple only ships `user` (name/email) on the very first grant.
      const givenName = result?.user?.name?.firstName || undefined;
      const familyName = result?.user?.name?.lastName || undefined;
      const email = result?.user?.email || undefined;

      await axios.post(
        `${API}/auth/apple`,
        {
          identity_token: idToken,
          nonce: nonceRef.current,
          email,
          given_name: givenName,
          family_name: familyName,
        },
        { withCredentials: true }
      );
      onSuccess?.();
    } catch (err) {
      // Apple raises `{error: "popup_closed_by_user"}` when the user
      // dismisses the sheet — treat that as a silent cancel.
      const code = err?.error || err?.code;
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
        return;
      }
      toast.error("Apple sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className="w-full flex items-center justify-center gap-3 rounded-full bg-black text-white h-12 px-6 hover:bg-[#161616] active:scale-[0.99] disabled:opacity-70 transition"
      data-testid="apple-web-signin-btn"
    >
      <svg
        aria-hidden="true"
        className="w-4 h-4"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M16.365 12.902c-.02-2.06 1.68-3.055 1.756-3.104-.957-1.4-2.446-1.591-2.977-1.615-1.267-.13-2.472.744-3.116.744-.65 0-1.638-.725-2.694-.703-1.386.02-2.667.806-3.379 2.05-1.442 2.498-.369 6.194 1.037 8.223.687.995 1.504 2.11 2.578 2.072 1.037-.043 1.428-.671 2.68-.671 1.252 0 1.605.671 2.7.65 1.115-.02 1.822-1.014 2.503-2.014.79-1.156 1.113-2.286 1.132-2.343-.025-.011-2.173-.833-2.22-3.289zM14.53 6.638c.573-.696.96-1.66.855-2.622-.826.033-1.826.548-2.418 1.244-.53.617-.994 1.6-.87 2.545.921.071 1.86-.47 2.433-1.167z" />
      </svg>
      <span className="text-sm font-medium">{busy ? "Signing you in…" : label}</span>
    </button>
  );
};

export default AppleSignInWebButton;
